<template>
  <div class="signup-invite-form">
    <div v-if="!inviteNotFound">
      <h2 class="text-campton-bold">{{ $gettext('You are invited to join:') + ' ' + companyName}}</h2>
      <p>{{ inviteMessage }}</p>
      <p class="text-danger" v-if="showResponseWarning">{{ responseWarningText }}</p>
    </div>
    <div v-if="inviteNotFound">
      <h2 class="text-campton-bold">{{ $gettext('Invitation not found') }}</h2>
      <p class="text-danger">{{ $gettext('This invitation link is invalid or has already been used. Please verify the link or ask the person who invited you to send a new invitation.') }}</p>
      <p class="alert alert-primary">
        {{ $gettext('You can also') + ' ' }}
        <a :href="Urls['accounts_sign_up']()">
          {{ $gettext('Sign up') }}
        </a>
        {{ ' ' + $gettext('or') + ' ' }}
        <router-link :to="{name: 'Login'}">
          {{ $gettext('log in') }}
        </router-link>
        {{ ' ' + $gettext('to continue.') }}
      </p>
    </div>

    <b-form v-if="!inviteNotFound" @submit.prevent="submitSignupInvite" class="gk-clear-form">
      <div>
        <b-form-group id="fieldset-signup-email" class="form-group floating-label">
          <b-form-input 
            type="email"
            id="input-signup-email"
            placeholder=" "
            class="floating-label" :class="{ 'is-touch': emailTouch }"
            v-model="email"
            :state="emailState"
            trim required
            @blur="emailTouch = true"
          />
          <label class="floating-label">{{ $gettext('Email address') }}</label>
          <small v-if="showEmailWarning || (emailTouch && !emailState)" class="email-hint form-text">
            <transition name="fade" appear mode="out-in">
              <span v-if="showEmailWarning" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-warning" />{{ emailWarningTxt }}
              </span>
              <span v-else-if="emailTouch && !emailState" :key="showEmailWarning" class="text-danger">
                <font-awesome-icon icon="fa-regular fa-circle-xmark" />{{ $gettext('Please enter a valid email address') }}
              </span>
            </transition>
          </small>
        </b-form-group>
      </div>

      <b-form-group id="fieldset-signup-password" class="form-group floating-label">
        <div class="position-relative">
          <b-form-input
            type="password"
            id="input-signup-password"
            placeholder=" "
            class="floating-label" :class="{ 'is-touch': passwordTouch }"
            v-model="password"
            :state="passwordState"
            trim required
            @blur="passwordTouch = true"
          />
          <label class="floating-label">{{ $gettext('Password') }}</label>
          <ShowPasswordIcon input-id="input-signup-password" />
        </div>
        <PasswordStrengthHint v-if="showPasswordHint" :password="password" @score-changed="(score) => { passwordScore = score }" @password-length-exceeded="(score) => { passwordScore = score }" />
      </b-form-group>

      <div style="padding-top: 0.5rem; width: 100%;">
        <GekkoButton
          type="submit"
          :text="$gettext('Get started')"
          :loading="loadingData || savingData"
          :disabled="(passwordScore < 1)"
          variant="primary"
          class="w-100 text-campton-book position-relative"
          size="xl"
        />
      </div>
    </b-form>

    <div v-if="!inviteNotFound">
      <div class="gk-divider">
        <div class="divider-line"></div>
        <div class="divider-text">{{ $gettext('or') }}</div>
        <div class="divider-line"></div>
      </div>
      <GoogleSSOButton 
        ref="googleSSOButton"
        @country-not-set="$emit('show-country', false)" 
        @authenticatedWithGoogle="handleThirdPartyAuth"
        :businesslocation="null" 
        :preferredlanguage="preferredlanguage"
        :inviteCode="inviteCode"
      />
      <AppleSSOButton 
        ref="appleSSOButton"
        @country-not-set="$emit('show-country', false)" 
        @authenticatedWithApple="handleThirdPartyAuth"
        :businesslocation="null" 
        :preferredlanguage="preferredlanguage"
        :inviteCode="inviteCode"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from "vuex"
import { randomCelebName, randomEmail } from "@/utils/"
import UserInviteService from '@/store/services/userinvite.service'
import GoogleSSOButton from "@/components/auth/GoogleSSOButton.vue"
import AppleSSOButton from "@/components/auth/AppleSSOButton.vue"
import GekkoButton from "@/components/GekkoButton"
import ShowPasswordIcon from "@/components/ui/ShowPasswordIcon"
import PasswordStrengthHint from "@/components/ui/PasswordStrengthHint"
import CountrySelector from "@/components/signup/CountrySelector"

export default {
  name: 'SignupInviteForm',
  components: {
    GekkoButton,
    ShowPasswordIcon,
    PasswordStrengthHint,
    GoogleSSOButton,
    AppleSSOButton,
    CountrySelector
  },
  data () {
    return {
      email: '',
      companyName: '',
      inviteMessage: '',
      inviteNotFound: false,
      preferredlanguage: '',
      password: '',
      showEmailWarning: false,
      emailWarningTxt: '',
      passwordScore: 0,
      loading: false,
      loadingUserLocation: false,
      savingData: false,
      emailTouch: false,
      passwordTouch: false,
      inviteCode: null,
      showResponseWarning: false,
      responseWarningText: ''
    }
  },
  computed: {
    ...mapGetters({
      userCountry: 'signup/getUserCountry',
      userLanguage: 'signup/getUserLanguage',
    }),
    randomPlaceholderName () {
      return randomCelebName()
    },
    randomPlaceholderEmail () {
      return randomEmail(this.randomPlaceholderName)
    },
    loadingData () {
      return this.loading || this.loadingUserLocation
    },
    showPasswordHint () {
      return (this.password.length > 0)
    },
    emailState () {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(this.email) && !this.showEmailWarning
    },
    passwordState () {
      return (this.passwordScore >= 1)
    }
  },
  created () {
    this.inviteCode = this.$route.query.invite_code && this.$route.query.invite_code !== '' ? this.$route.query.invite_code : null

    if (!this.inviteCode) {
      this.inviteNotFound = true
      this.setupData()
    } else {
      UserInviteService.getInviteDetails(this.inviteCode).then((response) => {
        this.email = response.data.email
        this.companyName = response.data.company_name
        this.inviteMessage = response.data.message
      }).catch(() => {
        this.inviteNotFound = true
      }).finally(() => {
        this.setupData()
      })
    }
  },
  methods: {
    ...mapActions({ 
      saveData: 'auth/signUpInvite',
      geoLocateUserIp: 'signup/geoLocateUserIp',
      getAccessToken: 'auth/getAccessToken',
      checkAvailability: 'signup/checkAvailability'
    }),
    setupData () {
      this.loadingUserLocation = true
      this.geoLocateUserIp().then(() => {
        this.preferredlanguage = this.$route.params.language || this.userLanguage
        this.loadingUserLocation = false
      })

    },
    submitSignupInvite () {
      this.loading = true
      return this.checkAvailability(this.email).then((response) => {
        this.loginSignupUser()
      }).catch((error) => {
        if (error.status === 409) { // We don't check if the email exists because we allow existing users to join teams
          this.loginSignupUser()
        } else if (error.status === 422) {
          this.showEmailWarning = true
          this.emailWarningTxt = this.$gettext('That email doesn\'t seem to be ok')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    loginSignupUser() {
      this.showEmailWarning = false
      if (this.passwordScore > 0) {
        const data = {
            email: this.email,
            password: this.password,
            preferred_language: this.preferredlanguage,
            invite_code: this.inviteCode
        }
        this.savingData = true
        
        this.saveData(data).then((response) => {
            if (response.status === 208) {
              this.showResponseWarning = true
              this.responseWarningText = this.$gettext('You are already connected to this company!')

              setTimeout(() => {
                this.$emit('authorised', false)
              }, 1500)
            } else {
              this.$emit('authorised', false)
            }
        }).catch((error) => {
            console.log('error', error)
            if ([400, 404].includes(error.status)) {
              this.$store.commit('setAlert', {
                title: this.$gettext("Invitation error"),
                text: error.data,
                variant: 'danger',
                duration: 4
              })
            } else if (error.status === 401) {
              this.$store.commit('setAlert', {
                title: this.$gettext("Authentication error"),
                text: this.$gettext('Incorrect password.'),
                variant: 'danger',
                duration: 4
              })
            }

            this.showResponseWarning = true
            this.responseWarningText = this.$gettext('Team connection failed.')
        }).finally(() => {
            this.savingData = false
            this.loading = false
        })
      }
    },
    handleThirdPartyAuth(is_authenticated) {
      this.loading = false
      if (is_authenticated) {
        this.$emit('authorised', false)
      } else {
        this.savingData = false
        this.showResponseWarning = true
        this.responseWarningText = this.$gettext('Third party authentication failed.')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.signup-invite-form {
  .email-hint {
    span {
      .svg-inline--fa {
        margin-right: 0.25rem
      }
    }
  }

  .form-control {
    &:not(.is-touch) {
      &.is-invalid, &.is-valid, &.is-invalid:focus, &.is-valid:focus {
        border: 1px solid #dfdfdf;
      }
    }
    &.is-touch {
      &.is-invalid {
        border: 1px solid $colorDanger;
      }
    }
  }
}
</style>